import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  AlertCircle,
  Lock,
  ChevronLeft,
  AlertTriangle,
  ShieldCheck,
  Eye,
  EyeOff,
  Phone,
  Truck,
  FileText,
  Package,
  Store,
  CheckCircle2,
  Delete,
  ChevronRight,
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { db, doc, getDoc, updateDoc, arrayUnion } from '../services/firebase';
import { APP_CONFIG } from '../utils/config';
import logo from '../assets/logo.png';

const generateSessionId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const DEV_PHONE = '8051725780';
const OWNER_PHONE = '9470248156';

const PHONE_MAX = 3;
const PHONE_TIERS = [120, 600, -1];
const PIN_MAX = 5;
const PIN_TIERS = [60, 300, 1800, -1];
const fmtTime = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

const readPhoneLock = () => {
  try {
    const raw = localStorage.getItem('lg_phone_lock');
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d.until !== -1 && d.until <= Date.now()) {
      localStorage.removeItem('lg_phone_lock');
      return null;
    }
    return d;
  } catch {
    return null;
  }
};
const writePhoneLock = (until, count, tier) =>
  localStorage.setItem('lg_phone_lock', JSON.stringify({ until, count, tier }));
const clearPhoneLock = () => localStorage.removeItem('lg_phone_lock');

// Phone verification cache - valid number ka result 5 min cache karo, no repeat DB call
const PHONE_CACHE_TTL = 5 * 60 * 1000;
const getPhoneCache = (ph) => {
  try {
    const raw = localStorage.getItem('lg_phone_cache');
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d.phone !== ph || Date.now() - d.ts > PHONE_CACHE_TTL) {
      localStorage.removeItem('lg_phone_cache');
      return null;
    }
    return d;
  } catch {
    return null;
  }
};
const setPhoneCache = (ph) =>
  localStorage.setItem('lg_phone_cache', JSON.stringify({ phone: ph, ts: Date.now() }));
const clearPhoneCache = () => localStorage.removeItem('lg_phone_cache');

const getRecent = () => {
  try {
    return JSON.parse(localStorage.getItem('lg_recent_phones') || '[]');
  } catch {
    return [];
  }
};
const saveRecent = (ph, name) => {
  try {
    const list = getRecent().filter((r) => r.phone !== ph);
    list.unshift({ phone: ph, name: name || '' });
    localStorage.setItem('lg_recent_phones', JSON.stringify(list.slice(0, 3)));
  } catch {}
};

export default function Login({ onLogin }) {
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [showPin, setShowPin] = useState(false);

  const initLock = readPhoneLock();
  const [phoneLockUntil, setPhoneLockUntil] = useState(initLock?.until ?? 0);
  const [phoneLockTier, setPhoneLockTier] = useState(initLock?.tier ?? 0);
  const [phoneAttempts, setPhoneAttempts] = useState(initLock?.count ?? 0);
  const [phoneCountdown, setPhoneCountdown] = useState(0);

  const [pinAttempts, setPinAttempts] = useState(0);
  const [pinLockTier, setPinLockTier] = useState(0);
  const [pinLockUntil, setPinLockUntil] = useState(0);
  const [pinCountdown, setPinCountdown] = useState(0);
  const [pinBlocked, setPinBlocked] = useState(false);

  // Countdown timer for Phone lockout
  useEffect(() => {
    if (phoneLockUntil === -1 || phoneLockUntil <= Date.now()) {
      setPhoneCountdown(0);
      return;
    }
    const tick = () => {
      const r = Math.ceil((phoneLockUntil - Date.now()) / 1000);
      if (r <= 0) {
        setPhoneCountdown(0);
        setPhoneLockUntil(0);
        setPhoneAttempts(0);
        setError('');
        clearPhoneLock();
      } else {
        setPhoneCountdown(r);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [phoneLockUntil]);

  // Countdown timer for PIN lockout
  useEffect(() => {
    if (pinBlocked || pinLockUntil <= Date.now()) {
      setPinCountdown(0);
      return;
    }
    const tick = () => {
      const r = Math.ceil((pinLockUntil - Date.now()) / 1000);
      if (r <= 0) {
        setPinCountdown(0);
        setPinLockUntil(0);
        setPinAttempts(0);
        setError('');
      } else {
        setPinCountdown(r);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [pinLockUntil, pinBlocked]);

  // Fetch lock status on step 'pin'
  useEffect(() => {
    if (step !== 'pin') return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', phone));
        if (!snap.exists()) return;
        const d = snap.data();
        if (d.lockUntil) {
          const until = new Date(d.lockUntil).getTime();
          if (until > Date.now()) setPinLockUntil(until);
        }
        setPinAttempts(d.loginAttempts || 0);
        setPinLockTier(d.lockTier || 0);
      } catch {}
    })();
  }, [step, phone]);

  const handlePhoneSubmit = async () => {
    if (phone.length !== 10) return;
    if (phoneLockUntil === -1) {
      setError('Device blocked. Contact admin.');
      return;
    }
    if (phoneLockUntil > Date.now()) {
      setError(`Too many attempts. Wait ${fmtTime(phoneCountdown)}.`);
      return;
    }
    setLoading(true);
    setError('');

    if (phone === DEV_PHONE) {
      setLoading(false);
      setPhoneAttempts(0);
      clearPhoneLock();
      setStep('pin');
      return;
    }

    if (getPhoneCache(phone)) {
      setLoading(false);
      setPhoneAttempts(0);
      clearPhoneLock();
      setStep('pin');
      return;
    }

    try {
      const snap = await getDoc(doc(db, 'users', phone));
      if (!snap.exists()) {
        const newCount = phoneAttempts + 1;
        if (newCount >= PHONE_MAX) {
          const newTier = phoneLockTier + 1;
          const duration = PHONE_TIERS[Math.min(newTier - 1, PHONE_TIERS.length - 1)];
          if (duration === -1) {
            setPhoneLockUntil(-1);
            setPhoneAttempts(newCount);
            setPhoneLockTier(newTier);
            writePhoneLock(-1, newCount, newTier);
            setError('Device blocked. Contact admin.');
          } else {
            const until = Date.now() + duration * 1000;
            setPhoneLockUntil(until);
            setPhoneAttempts(0);
            setPhoneLockTier(newTier);
            writePhoneLock(until, 0, newTier);
            setError(`Too many attempts. Locked for ${fmtTime(duration)}.`);
          }
        } else {
          setPhoneAttempts(newCount);
          writePhoneLock(0, newCount, phoneLockTier);
          const left = PHONE_MAX - newCount;
          setError(`Unregistered mobile number. (${left} attempt${left === 1 ? '' : 's'} left)`);
          setPhone('');
        }
        setLoading(false);
        return;
      }

      setPhoneCache(phone);
      setPhoneAttempts(0);
      clearPhoneLock();
      setStep('pin');
    } catch {
      setError('Network error. Check connection and try again.');
    }
    setLoading(false);
  };

  const handlePinSubmit = async () => {
    if (pin.length < 4) return;
    if (pinBlocked) {
      setError('Account blocked. Contact admin.');
      return;
    }
    if (pinLockUntil > Date.now()) {
      setError(`Locked. Wait ${fmtTime(pinCountdown)}.`);
      setPin('');
      return;
    }
    setLoading(true);
    setError('');

    try {
      if (phone === DEV_PHONE) {
        if (pin !== '0000') {
          handleWrongPin();
          return;
        }
        await resetPinAttempts();
        await createSession({ phone: DEV_PHONE, name: 'Developer', role: 'admin' });
        return;
      }

      const snap = await getDoc(doc(db, 'users', phone));
      if (!snap.exists()) {
        setError('User record not found.');
        setLoading(false);
        return;
      }

      const data = snap.data();
      if (data.blocked && pin !== data.pin) {
        handleWrongPin();
        return;
      }
      if (data.blocked && pin === data.pin) {
        await resetPinAttempts();
        await createSession({ ...data, phone, blocked: true });
        return;
      }
      if (pin !== data.pin) {
        handleWrongPin();
        return;
      }

      await resetPinAttempts();
      if (data.role === 'admin') {
        await createSession({ ...data, phone });
        return;
      }

      if (data.activeSession && data.sessionExpiry && new Date(data.sessionExpiry) > new Date()) {
        setPendingUser({ ...data, phone });
        setStep('conflict');
        setLoading(false);
        return;
      }

      await createSession({ ...data, phone });
    } catch {
      setError('Network error. Try again.');
      setLoading(false);
    }
  };

  const handleWrongPin = async () => {
    const newAttempts = pinAttempts + 1;
    setPinAttempts(newAttempts);
    setPin('');
    setLoading(false);
    clearPhoneCache();

    if (newAttempts >= PIN_MAX) {
      const newTier = pinLockTier + 1;
      setPinLockTier(newTier);
      const duration = PIN_TIERS[Math.min(newTier - 1, PIN_TIERS.length - 1)];
      if (duration === -1) {
        setPinBlocked(true);
        try {
          await updateDoc(doc(db, 'users', phone), {
            blocked: true,
            blockedAt: new Date().toISOString(),
            blockReason: 'Too many wrong PIN attempts',
            loginAttempts: 0,
            lockTier: newTier,
          });
        } catch {}
        setError('Account permanently blocked. Contact admin.');
      } else {
        const until = Date.now() + duration * 1000;
        setPinLockUntil(until);
        setPinAttempts(0);
        try {
          await updateDoc(doc(db, 'users', phone), {
            loginAttempts: 0,
            lockUntil: new Date(until).toISOString(),
            lockTier: newTier,
          });
        } catch {}
        setError(`Wrong PIN. Locked for ${fmtTime(duration)}.`);
      }
    } else {
      const left = PIN_MAX - newAttempts;
      try {
        await updateDoc(doc(db, 'users', phone), {
          loginAttempts: newAttempts,
          lockTier: pinLockTier,
        });
      } catch {}
      setError(`Incorrect PIN — ${left} attempt${left === 1 ? '' : 's'} left`);
    }
  };

  const resetPinAttempts = async () => {
    setPinAttempts(0);
    setPinLockUntil(0);
    setPinLockTier(0);
    setPinBlocked(false);
    try {
      await updateDoc(doc(db, 'users', phone), { loginAttempts: 0, lockUntil: null, lockTier: 0 });
    } catch {}
  };

  const createSession = async (userData) => {
    const sessionId = generateSessionId();
    const isDev = userData.phone === DEV_PHONE;
    let timeoutHours = 24;
    try {
      const s = await getDoc(doc(db, 'settings', 'app'));
      if (s.exists() && s.data().sessionTimeout) timeoutHours = s.data().sessionTimeout;
    } catch {}

    const sessionExpiry = new Date(Date.now() + (isDev ? 4 : timeoutHours) * 3600000).toISOString();
    saveRecent(userData.phone, userData.name);

    try {
      const userRef = doc(db, 'users', userData.phone);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const ua = navigator.userAgent || '';
        const upd = {
          activeSession: sessionId,
          sessionExpiry,
          lastLogin: new Date().toISOString(),
          lastDevice: ua.slice(0, 100),
        };
        if (userSnap.data().role === 'admin') {
          const device = ua.includes('iPhone')
            ? 'iPhone'
            : ua.includes('Android')
            ? 'Android'
            : ua.includes('Windows')
            ? 'Windows PC'
            : ua.includes('Mac')
            ? 'Mac'
            : 'Unknown';
          const browser = ua.includes('Firefox')
            ? 'Firefox'
            : ua.includes('Edg')
            ? 'Edge'
            : ua.includes('Chrome')
            ? 'Chrome'
            : ua.includes('Safari')
            ? 'Safari'
            : 'Other';
          const os = ua.includes('Android')
            ? 'Android'
            : ua.includes('iPhone')
            ? 'iOS'
            : ua.includes('Windows')
            ? 'Windows'
            : ua.includes('Mac')
            ? 'macOS'
            : ua.includes('Linux')
            ? 'Linux'
            : 'Unknown';
          upd.sessions = arrayUnion({
            sessionId,
            device,
            browser,
            os,
            loginAt: new Date().toISOString(),
            expiresAt: sessionExpiry,
          });
        }
        await updateDoc(userRef, upd);
      }
    } catch {}

    const sessionData = { ...userData, sessionId, sessionExpiry };
    localStorage.setItem(`lg_user_${userData.phone}`, JSON.stringify(sessionData));
    localStorage.setItem('lg_user', JSON.stringify(sessionData));
    localStorage.setItem('lg_last_login', userData.phone);
    sessionStorage.setItem('lg_active_phone', userData.phone);
    onLogin(sessionData);
  };

  const terminateAndLogin = async () => {
    setLoading(true);
    await createSession(pendingUser);
    setLoading(false);
  };

  const NumPad = ({ onPress, disabled }) => {
    const t = useRef(null);
    const keys = [
      { key: 1, sub: '' },
      { key: 2, sub: 'ABC' },
      { key: 3, sub: 'DEF' },
      { key: 4, sub: 'GHI' },
      { key: 5, sub: 'JKL' },
      { key: 6, sub: 'MNO' },
      { key: 7, sub: 'PQRS' },
      { key: 8, sub: 'TUV' },
      { key: 9, sub: 'WXYZ' },
      { key: 'clear', sub: '', label: 'CLR' },
      { key: 0, sub: '+' },
      { key: 'back', sub: '', isIcon: true },
    ];

    return (
      <div className="bg-slate-50/80 p-2 sm:p-2.5 rounded-2xl border border-slate-200/80 mt-4 max-w-[320px] mx-auto select-none shadow-2xs">
        <div className="grid grid-cols-3 gap-2">
          {keys.map((k, i) => (
            <motion.button
              key={i}
              type="button"
              whileTap={!disabled ? { scale: 0.93 } : {}}
              transition={{ duration: 0.05 }}
              onClick={() => !disabled && (k.key === 'clear' ? onPress('CLEAR') : onPress(k.key))}
              onPointerDown={() => {
                if (k.key === 'back' && !disabled) t.current = setTimeout(() => onPress('CLEAR'), 500);
              }}
              onPointerUp={() => clearTimeout(t.current)}
              onPointerLeave={() => clearTimeout(t.current)}
              disabled={disabled}
              className={`h-12 rounded-xl flex flex-col items-center justify-center transition-all cursor-pointer ${
                k.key === 'clear'
                  ? 'bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-500 border border-slate-200/80 shadow-2xs'
                  : k.key === 'back'
                  ? 'bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-700 border border-slate-200/80 shadow-2xs'
                  : 'bg-white hover:bg-slate-50 active:bg-blue-50 active:border-blue-300 text-slate-900 border border-slate-200/90 shadow-2xs'
              } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {k.isIcon ? (
                <Delete size={18} className="text-slate-700" />
              ) : k.label ? (
                <span className="font-bold text-[11px] uppercase tracking-wider text-slate-500">{k.label}</span>
              ) : (
                <>
                  <span className="text-lg sm:text-xl font-bold text-slate-900 leading-none">{k.key}</span>
                  {k.sub && (
                    <span className="text-[9px] font-semibold text-slate-400 tracking-wider leading-none mt-0.5">
                      {k.sub}
                    </span>
                  )}
                </>
              )}
            </motion.button>
          ))}
        </div>
      </div>
    );
  };

  const isPhoneLocked = phoneLockUntil === -1 || phoneLockUntil > Date.now();
  const isPinLocked = pinLockUntil > Date.now() || pinBlocked;
  const recent = getRecent();

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 flex flex-col lg:flex-row selection:bg-blue-100 selection:text-blue-900">
      
      {/* ── LEFT SHOWCASE PANEL (DESKTOP) ── */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-[42%] bg-[#0b1b2b] text-white flex-col justify-between p-10 xl:p-14 shrink-0 relative overflow-hidden">
        {/* Subtle Ambient Glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Top: Brand Badge & Return Link */}
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-2.5 group">
              <img
                src={logo}
                alt="Lucy Garden"
                className="w-10 h-10 rounded-xl object-contain bg-white/10 p-0.5 border border-white/20 shadow-md group-hover:scale-105 transition-transform"
              />
              <div>
                <span className="font-extrabold text-lg text-white tracking-tight leading-none block">
                  Lucy Garden
                </span>
                <span className="text-[11px] text-blue-200/70 font-medium block mt-0.5">
                  Amrit Dairy Wholesale · Bihar
                </span>
              </div>
            </Link>

            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-xs font-semibold text-slate-200 border border-white/10 transition-all"
            >
              <ChevronLeft size={13} />
              <span>Back to Portal</span>
            </Link>
          </div>
        </div>

        {/* Middle: Enterprise Value Proposition & Feature Cards */}
        <div className="relative z-10 my-8 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-blue-500/15 text-blue-300 border border-blue-400/20">
            <ShieldCheck size={14} className="text-blue-400" />
            <span>Authorized B2B Retail Gateway</span>
          </div>

          <div>
            <h1 className="text-3xl xl:text-4xl font-extrabold text-white tracking-tight leading-tight">
              Enterprise Dairy Supply & Digital Ledger.
            </h1>
            <p className="text-sm text-slate-300 mt-2.5 leading-relaxed max-w-md">
              Supplying pasteurized milk pouches, malai paneer, dahi, ghee and sweets in bulk from Amrit Dairy, Bihar to 70+ retail partners every morning.
            </p>
          </div>

          {/* 3 Key Operational Pillars */}
          <div className="space-y-3 pt-2">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-300 flex items-center justify-center shrink-0 mt-0.5">
                <Truck size={16} />
              </div>
              <div>
                <p className="text-xs font-bold text-white">Daily 05:00 AM Doorstep Drop</p>
                <p className="text-[11px] text-slate-300 mt-0.5">
                  Chilled refrigerated delivery to your counter before morning customer rush.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center shrink-0 mt-0.5">
                <FileText size={16} />
              </div>
              <div>
                <p className="text-xs font-bold text-white">Live Digital Khata & Billing</p>
                <p className="text-[11px] text-slate-300 mt-0.5">
                  Real-time invoice history, daily crate counts, and online ledger transparency.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-300 flex items-center justify-center shrink-0 mt-0.5">
                <Package size={16} />
              </div>
              <div>
                <p className="text-xs font-bold text-white">100% Genuine Factory Sourcing</p>
                <p className="text-[11px] text-slate-300 mt-0.5">
                  Bulk procurement straight from Amrit Dairy (Ganga Dairy Ltd.), Bihar.
                </p>
              </div>
            </div>
          </div>

          {/* Metrics Pill Row */}
          <div className="flex items-center gap-3 pt-2 text-xs font-semibold text-slate-300">
            <span className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 rounded-md">
              <Store size={12} className="text-emerald-400" />
              70+ Retailers
            </span>
            <span className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 rounded-md">
              <CheckCircle2 size={12} className="text-blue-400" />
              45 Official SKUs
            </span>
            <span className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 rounded-md">
              Bihar Distribution
            </span>
          </div>
        </div>

        {/* Bottom: Owner Contact, System Info & Makeward Labs Branding */}
        <div className="relative z-10 pt-6 border-t border-white/10 space-y-2.5 text-xs text-slate-400">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Phone size={13} className="text-slate-400" />
              <span>New shop registration: <strong className="text-white">+91 {OWNER_PHONE}</strong></span>
            </div>
            <span className="font-mono text-[11px] text-slate-500">v{APP_CONFIG.version}</span>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-white/5 text-[11px]">
            <span>&copy; {new Date().getFullYear()} {APP_CONFIG.appName}. All rights reserved.</span>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400">Engineered by</span>
              <a
                href={APP_CONFIG.developer.portfolio}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/10 hover:bg-white/15 border border-white/10 font-bold text-white hover:text-white transition-all shadow-2xs group"
              >
                <span className="w-4 h-4 rounded bg-white flex items-center justify-center p-[1.5px] shadow-xs shrink-0">
                  <img src="/MAKEWARD_logo.png" alt="Makeward Labs" className="w-full h-full object-contain" />
                </span>
                <span className="tracking-tight">{APP_CONFIG.developer.name}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── RIGHT AUTHENTICATION PANEL (MOBILE & DESKTOP) ── */}
      <div className="flex-1 flex flex-col min-h-screen bg-white sm:bg-[#f8fafc]">
        
        {/* Mobile Top Header (Clean, Native & Professional) */}
        <div className="lg:hidden bg-white border-b border-slate-200/80 px-4 py-2.5 flex items-center justify-between shadow-2xs">
          <Link to="/" className="inline-flex items-center gap-2.5 min-w-0">
            <img src={logo} alt="Lucy Garden" className="w-8 h-8 rounded-lg object-contain bg-slate-50 border border-slate-200 p-0.5 shrink-0" />
            <div className="min-w-0">
              <span className="font-extrabold text-sm text-slate-900 tracking-tight leading-none block truncate">
                Lucy Garden
              </span>
              <span className="text-[10px] text-slate-500 font-medium block mt-0.5 truncate">
                Amrit Dairy Wholesale · Bihar
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-full">
              <ShieldCheck size={11} className="text-emerald-600" />
              <span>B2B Portal</span>
            </span>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors"
            >
              <ChevronLeft size={13} />
              <span>Home</span>
            </Link>
          </div>
        </div>

        {/* 2-Step Micro Progress Line */}
        <div className="lg:hidden w-full h-1 bg-slate-100">
          <div
            className={`h-full bg-blue-900 transition-all duration-300 ${
              step === 'phone' ? 'w-1/2' : 'w-full'
            }`}
          />
        </div>

        {/* Center Content Form Area */}
        <div className="flex-1 flex items-center justify-center p-3 sm:p-8 lg:p-12">
          <div className="w-full max-w-[390px] min-w-0 my-auto">
            
            {/* Auth Card Box */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs sm:shadow-lg p-4 sm:p-7">
              <AnimatePresence mode="wait">
                
                {/* ── STEP 1: PHONE VERIFICATION ── */}
                {step === 'phone' && (
                  <motion.div
                    key="phone-step"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.18 }}
                  >
                    {/* Step Badge */}
                    <div className="flex items-center justify-between mb-3.5">
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-blue-900 bg-blue-50 px-2.5 py-0.5 rounded-md border border-blue-200/80">
                        <ShieldCheck size={11} className="text-blue-700" />
                        <span>Step 1 of 2 · Mobile</span>
                      </span>
                      <span className="text-[11px] font-semibold text-slate-400">
                        Retail Verification
                      </span>
                    </div>

                    <div className="mb-4 sm:mb-5">
                      <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                        Retailer Sign In
                      </h2>
                      <p className="text-xs text-slate-500 mt-1">
                        Enter your 10-digit registered mobile number to proceed.
                      </p>
                    </div>

                    {/* Recent Saved Accounts on Device */}
                    {phone === '' && recent.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                            Saved Quick Login
                          </p>
                          <span className="text-[10px] text-blue-900 font-semibold">1-Tap Sign In</span>
                        </div>
                        <div className="space-y-1.5">
                          {recent.map((r) => (
                            <motion.button
                              key={r.phone}
                              type="button"
                              whileTap={{ scale: 0.98 }}
                              onClick={() => {
                                setPhone(r.phone);
                                setError('');
                              }}
                              className="w-full flex items-center justify-between p-2.5 sm:p-3 bg-gradient-to-r from-slate-50 via-white to-blue-50/30 hover:to-blue-100/40 rounded-xl border border-slate-200 hover:border-blue-400 transition-all text-left group cursor-pointer shadow-2xs"
                            >
                              <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-blue-950 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-2xs ring-2 ring-blue-900/10">
                                  {(r.name || r.phone).slice(0, 2).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  {r.name && (
                                    <p className="text-xs font-bold text-slate-900 truncate group-hover:text-blue-950">
                                      {r.name}
                                    </p>
                                  )}
                                  <p className="text-[11px] font-mono text-slate-500 font-medium">
                                    +91 &bull;&bull;&bull;&bull;&bull; {r.phone.slice(-5)}
                                  </p>
                                </div>
                              </div>
                              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-950 bg-blue-50 border border-blue-200 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg group-hover:bg-blue-950 group-hover:text-white transition-all shrink-0">
                                <span>Select</span>
                                <ChevronRight size={12} />
                              </span>
                            </motion.button>
                          ))}
                        </div>
                        <div className="flex items-center gap-3 my-3 sm:my-4">
                          <div className="flex-1 h-px bg-slate-200" />
                          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                            or enter number
                          </span>
                          <div className="flex-1 h-px bg-slate-200" />
                        </div>
                      </div>
                    )}

                    {/* Phone Input Box */}
                    <div className="space-y-1">
                      <label className="block text-xs font-bold text-slate-700">
                        Registered Mobile Number
                      </label>
                      <motion.div
                        animate={error ? { x: [0, -6, 6, -4, 4, 0] } : {}}
                        transition={{ duration: 0.2 }}
                        className={`flex items-center gap-2 sm:gap-2.5 rounded-xl px-3 sm:px-3.5 py-2.5 sm:py-3 border-2 transition-all overflow-hidden ${
                          error
                            ? 'border-red-400 bg-red-50/30'
                            : phone.length > 0
                            ? 'border-blue-900 bg-white ring-4 ring-blue-900/5'
                            : 'border-slate-200 bg-slate-50/70 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-1 text-xs font-bold text-slate-700 bg-slate-100 px-2 py-1 rounded-md shrink-0 select-none">
                          <span className="text-xs leading-none">🇮🇳</span>
                          <span>+91</span>
                        </div>
                        <div className="w-px h-5 bg-slate-200 shrink-0" />
                        <input
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          value={phone}
                          autoFocus
                          readOnly={window.innerWidth < 1024}
                          onFocus={(e) => {
                            // Only allow keyboard on lg+ (desktop); blur immediately on mobile
                            if (window.innerWidth < 1024) e.target.blur();
                          }}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setPhone(v);
                            setError('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handlePhoneSubmit();
                          }}
                          placeholder="Enter 10 digits"
                          className="flex-1 min-w-0 w-full text-base sm:text-lg font-bold font-mono tracking-wider bg-transparent outline-none text-slate-900 placeholder:text-slate-300 placeholder:font-normal placeholder:tracking-normal"
                        />
                        {phone.length === 10 ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200 shrink-0 select-none">
                            <CheckCircle2 size={12} className="text-emerald-600" />
                            <span>10/10</span>
                          </span>
                        ) : phone.length > 0 ? (
                          <span className="text-[11px] font-mono font-medium text-slate-400 shrink-0 ml-1 select-none whitespace-nowrap">
                            {phone.length}/10
                          </span>
                        ) : null}
                      </motion.div>
                    </div>

                    {/* Error & Lockout Message */}
                    <div className="min-h-5 flex items-center mt-1.5">
                      <AnimatePresence>
                        {(error || phoneCountdown > 0 || phoneLockUntil === -1) && (
                          <motion.p
                            initial={{ opacity: 0, y: -2 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className={`text-xs font-medium flex items-center gap-1.5 ${
                              phoneLockUntil === -1 || phoneCountdown > 0
                                ? 'text-amber-600'
                                : 'text-red-600'
                            }`}
                          >
                            {phoneLockUntil === -1 || phoneCountdown > 0 ? (
                              <Lock size={12} className="shrink-0" />
                            ) : (
                              <AlertCircle size={12} className="shrink-0" />
                            )}
                            <span>
                              {phoneLockUntil === -1
                                ? 'Device blocked. Contact admin.'
                                : phoneCountdown > 0
                                ? `Locked · Wait ${fmtTime(phoneCountdown)}`
                                : error}
                            </span>
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* On-Screen Tactile NumPad (Mobile View) */}
                    <div className="lg:hidden">
                      <NumPad
                        disabled={isPhoneLocked}
                        onPress={(k) => {
                          setError('');
                          if (k === 'CLEAR') setPhone('');
                          else if (k === 'back') setPhone((p) => p.slice(0, -1));
                          else if (phone.length < 10) setPhone((p) => p + k);
                        }}
                      />
                    </div>

                    {/* Desktop Keyboard Hint */}
                    <p className="hidden lg:block text-xs text-slate-400 mt-2">
                      Type 10 digits on keyboard &middot; Press Enter to proceed
                    </p>

                    {/* Primary Continue Button */}
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      onClick={handlePhoneSubmit}
                      disabled={loading || phone.length < 10 || isPhoneLocked}
                      className={`w-full mt-4 py-3.5 px-4 rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all cursor-pointer ${
                        phone.length === 10 && !isPhoneLocked
                          ? 'bg-blue-950 text-white hover:bg-blue-900 shadow-md shadow-blue-950/20'
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <span>Continue to PIN</span>
                          <ArrowRight size={14} />
                        </>
                      )}
                    </motion.button>

                    {/* Onboarding Assistance Box */}
                    <div className="mt-5 pt-4 border-t border-slate-100 text-center">
                      <p className="text-xs text-slate-600">
                        Nayi dukaan registered nahi hai?
                      </p>
                      <div className="flex items-center justify-center gap-3 mt-1.5">
                        <a
                          href={`tel:+91${OWNER_PHONE}`}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-blue-900 transition-colors"
                        >
                          <Phone size={12} />
                          <span>Call Owner</span>
                        </a>
                        <span className="text-slate-300">&bull;</span>
                        <a
                          href={`https://wa.me/91${OWNER_PHONE}?text=${encodeURIComponent(
                            'Hello Lucy Garden, I want to onboard my retail shop for morning Amrit Dairy distribution in Bihar.'
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-800 transition-colors"
                        >
                          <FaWhatsapp size={13} />
                          <span>WhatsApp</span>
                        </a>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* ── STEP 2: PIN VERIFICATION ── */}
                {step === 'pin' && (
                  <motion.div
                    key="pin-step"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.18 }}
                  >
                    {/* Back to Phone Step */}
                    <button
                      onClick={() => {
                        setStep('phone');
                        setPin('');
                        setError('');
                      }}
                      className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-blue-950 transition-colors mb-3 cursor-pointer"
                    >
                      <ChevronLeft size={14} />
                      <span>Change Mobile Number</span>
                    </button>

                    {/* Step Header */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        Step 2 of 2
                      </span>
                      <span className="text-xs font-semibold text-slate-400">
                        Security PIN
                      </span>
                    </div>

                    <div className="mb-5">
                      <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                        Enter 4-Digit PIN
                      </h2>
                      <p className="text-xs font-mono text-slate-500 mt-1">
                        +91 {phone.slice(0, 5)} {phone.slice(5)}
                      </p>
                    </div>

                    {/* Discrete 4-Box Security PIN Display */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label className="block text-xs font-bold text-slate-700">
                          Account Security PIN
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowPin(!showPin)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-900 hover:text-blue-950 transition-colors cursor-pointer py-0.5 px-1.5 rounded hover:bg-blue-50"
                        >
                          {showPin ? <EyeOff size={13} /> : <Eye size={13} />}
                          <span>{showPin ? 'Hide PIN' : 'Show PIN'}</span>
                        </button>
                      </div>

                      <motion.div
                        animate={error ? { x: [0, -6, 6, -4, 4, 0] } : {}}
                        transition={{ duration: 0.2 }}
                        className="relative py-1.5"
                      >
                        {/* 4 Visual Digit Boxes */}
                        <div className="flex items-center justify-center gap-3">
                          {[0, 1, 2, 3].map((i) => {
                            const digit = pin[i];
                            const isCurrent = pin.length === i;
                            return (
                              <div
                                key={i}
                                className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center text-xl font-black transition-all ${
                                  error
                                    ? 'border-2 border-red-400 bg-red-50/40 text-red-700'
                                    : digit !== undefined
                                    ? 'border-2 border-blue-900 bg-blue-50/50 text-slate-900 shadow-xs'
                                    : isCurrent
                                    ? 'border-2 border-blue-600 bg-white ring-4 ring-blue-600/10'
                                    : 'border-2 border-slate-200 bg-slate-50/70 text-slate-300'
                                }`}
                              >
                                {digit !== undefined ? (
                                  showPin ? (
                                    digit
                                  ) : (
                                    <span className="w-3 h-3 rounded-full bg-slate-900 block" />
                                  )
                                ) : isCurrent ? (
                                  <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse block" />
                                ) : (
                                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300 block" />
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Hidden Accessible Input for Physical Keyboard / Screen Readers */}
                        <input
                          type="password"
                          inputMode="numeric"
                          maxLength={4}
                          value={pin}
                          autoFocus
                          readOnly={window.innerWidth < 1024}
                          onFocus={(e) => {
                            // Only allow keyboard on lg+ (desktop); blur immediately on mobile
                            if (window.innerWidth < 1024) e.target.blur();
                          }}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                            setPin(v);
                            setError('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handlePinSubmit();
                          }}
                          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                          aria-label="Account PIN"
                        />
                      </motion.div>
                    </div>

                    {/* Error & Lockout Message */}
                    <div className="min-h-6 flex items-center mt-2">
                      <AnimatePresence>
                        {(error || pinCountdown > 0 || pinBlocked) && (
                          <motion.p
                            initial={{ opacity: 0, y: -2 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className={`text-xs font-medium flex items-center gap-1.5 ${
                              pinBlocked || pinCountdown > 0 ? 'text-amber-600' : 'text-red-600'
                            }`}
                          >
                            {pinBlocked || pinCountdown > 0 ? (
                              <Lock size={12} className="shrink-0" />
                            ) : (
                              <AlertCircle size={12} className="shrink-0" />
                            )}
                            <span>
                              {pinBlocked
                                ? 'Account blocked. Contact admin.'
                                : pinCountdown > 0
                                ? `Locked · Wait ${fmtTime(pinCountdown)}`
                                : error}
                            </span>
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* On-Screen Tactile NumPad (Mobile View) */}
                    <div className="lg:hidden">
                      <NumPad
                        disabled={isPinLocked}
                        onPress={(k) => {
                          setError('');
                          if (k === 'CLEAR') setPin('');
                          else if (k === 'back') setPin((p) => p.slice(0, -1));
                          else if (pin.length < 4) setPin((p) => p + String(k));
                        }}
                      />
                    </div>

                    {/* Desktop Keyboard Hint */}
                    <p className="hidden lg:block text-xs text-slate-400 mt-2">
                      Type 4-digit PIN on keyboard &middot; Press Enter to verify
                    </p>

                    {/* Verify & Login Button */}
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      onClick={handlePinSubmit}
                      disabled={loading || pin.length < 4 || isPinLocked}
                      className={`w-full mt-4 py-3.5 px-4 rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all cursor-pointer ${
                        pin.length === 4 && !isPinLocked
                          ? 'bg-blue-950 text-white hover:bg-blue-900 shadow-md shadow-blue-950/20'
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <span>Verify & Sign In</span>
                          <ArrowRight size={14} />
                        </>
                      )}
                    </motion.button>

                    {/* Forgot PIN / Support */}
                    <div className="mt-5 pt-4 border-t border-slate-100 text-center">
                      <p className="text-xs text-slate-500">
                        PIN bhool gaye ya reset karana hai?
                      </p>
                      <a
                        href={`https://wa.me/91${OWNER_PHONE}?text=${encodeURIComponent(
                          `Hello Lucy Garden, I forgot my retail portal PIN for number +91 ${phone}. Please assist with PIN reset.`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-800 transition-colors mt-1.5"
                      >
                        <FaWhatsapp size={13} />
                        <span>Request PIN Reset on WhatsApp</span>
                      </a>
                    </div>
                  </motion.div>
                )}

                {/* ── STEP 3: SESSION CONFLICT RESOLUTION ── */}
                {step === 'conflict' && (
                  <motion.div
                    key="conflict-step"
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="text-center mb-6">
                      <div className="w-12 h-12 bg-amber-50 border border-amber-200 rounded-2xl flex items-center justify-center mx-auto mb-3 text-amber-600">
                        <AlertTriangle size={22} />
                      </div>
                      <h2 className="text-xl font-black text-slate-900 tracking-tight">
                        Active Session Detected
                      </h2>
                      <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                        This retail account is currently logged in on another device or browser. To continue here, terminate the other session.
                      </p>
                    </div>

                    <div className="space-y-2.5">
                      <motion.button
                        whileTap={{ scale: 0.98 }}
                        onClick={terminateAndLogin}
                        disabled={loading}
                        className="w-full py-3 rounded-xl font-bold text-xs sm:text-sm text-white bg-red-600 hover:bg-red-700 shadow-sm transition-colors cursor-pointer flex items-center justify-center gap-2"
                      >
                        {loading ? 'Please wait...' : 'Terminate Other Session & Login Here'}
                      </motion.button>

                      <motion.button
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setStep('phone');
                          setPin('');
                          setError('');
                        }}
                        className="w-full py-2.5 rounded-xl font-semibold text-xs text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors cursor-pointer"
                      >
                        Cancel
                      </motion.button>
                    </div>
                  </motion.div>
                )}

              </AnimatePresence>
            </div>

            {/* Bottom Footer Note & Makeward Labs Branding (Mobile only, desktop has left panel footer) */}
            <div className="lg:hidden mt-6 text-center text-[11px] text-slate-400 space-y-1">
              <p>
                &copy; {new Date().getFullYear()} {APP_CONFIG.appName} &middot; Authorized Wholesale Portal &middot; Bihar
              </p>
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
                <span>Engineered by</span>
                <a
                  href={APP_CONFIG.developer.portfolio}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-100 hover:bg-slate-200 border border-slate-200 font-bold text-slate-800 transition-colors"
                >
                  <span className="w-4 h-4 rounded bg-white flex items-center justify-center p-[1.5px] shadow-xs shrink-0 border border-slate-200/60">
                    <img src="/MAKEWARD_logo.png" alt="Makeward Labs" className="w-full h-full object-contain" />
                  </span>
                  <span>{APP_CONFIG.developer.name}</span>
                </a>
                <span className="font-mono text-[10px] text-slate-400">&bull; v{APP_CONFIG.version}</span>
              </div>
            </div>

          </div>
        </div>

      </div>

    </div>
  );
}
